import React from "react";
import { Alert, Col, Form, Row } from "react-bootstrap";
import AppSelect from "./ui/AppSelect";

export const emptyProductFulfillment = () => ({
  shippingMode: "STANDARD",
  packagedWeightKg: "",
  packageDimensionsCm: { length: "", width: "", height: "" },
  freightAboveQuantity: "",
  requiredCarrierType: "ANY",
  handlingClass: "STANDARD",
  requiresTeamLift: false,
});

export const normaliseProductFulfillment = (value = {}) => ({
  ...emptyProductFulfillment(),
  ...value,
  packagedWeightKg: value.packagedWeightKg ?? "",
  freightAboveQuantity: value.freightAboveQuantity ?? "",
  packageDimensionsCm: {
    ...emptyProductFulfillment().packageDimensionsCm,
    ...(value.packageDimensionsCm || {}),
  },
});

const optionalNumber = (value) =>
  value === "" || value === null || value === undefined ? undefined : Number(value);

export const serialiseProductFulfillment = (value = {}) => {
  const normalized = normaliseProductFulfillment(value);
  return {
    shippingMode: normalized.shippingMode,
    packagedWeightKg: optionalNumber(normalized.packagedWeightKg),
    packageDimensionsCm: {
      length: optionalNumber(normalized.packageDimensionsCm.length),
      width: optionalNumber(normalized.packageDimensionsCm.width),
      height: optionalNumber(normalized.packageDimensionsCm.height),
    },
    freightAboveQuantity: optionalNumber(normalized.freightAboveQuantity),
    requiredCarrierType: normalized.requiredCarrierType,
    handlingClass: normalized.handlingClass,
    requiresTeamLift: normalized.requiresTeamLift === true,
  };
};

export default function ProductFreightFields({
  product,
  setProduct,
  disabled = false,
  labelClassName,
  controlClassName,
}) {
  const fulfillment = normaliseProductFulfillment(product.fulfillment);
  const update = (patch) =>
    setProduct((current) => ({
      ...current,
      fulfillment: { ...normaliseProductFulfillment(current.fulfillment), ...patch },
    }));
  const updateDimension = (key, value) =>
    update({
      packageDimensionsCm: {
        ...normaliseProductFulfillment(product.fulfillment).packageDimensionsCm,
        [key]: value,
      },
    });

  return (
    <>
      <Alert variant={fulfillment.shippingMode === "ARRANGED_FREIGHT" ? "warning" : "light"}>
        <strong>Delivery classification:</strong>{" "}
        {fulfillment.shippingMode === "ARRANGED_FREIGHT"
          ? "Checkout excludes delivery. Lusuku sends a separate freight quote for customer approval before dispatch."
          : "Normal delivery pricing applies unless the packed order crosses an automatic weight or size threshold."}
      </Alert>
      <Row className="g-3 mb-3">
        <Col md={4}>
          <Form.Label className={labelClassName}>Shipping mode</Form.Label>
          <AppSelect block
            value={fulfillment.shippingMode}
            onChange={(event) => update({ shippingMode: event.target.value })}
            disabled={disabled}
            className={controlClassName}
          >
            <option value="STANDARD">Standard delivery</option>
            <option value="ARRANGED_FREIGHT">Arranged freight — quoted separately</option>
          </AppSelect>
        </Col>
        <Col md={4}>
          <Form.Label className={labelClassName}>Packed weight per unit (kg)</Form.Label>
          <Form.Control
            type="number"
            min="0"
            step="0.01"
            value={fulfillment.packagedWeightKg}
            onChange={(event) => update({ packagedWeightKg: event.target.value })}
            disabled={disabled}
            className={controlClassName}
            placeholder="e.g. 105"
          />
        </Col>
        <Col md={4}>
          <Form.Label className={labelClassName}>Freight from quantity</Form.Label>
          <Form.Control
            type="number"
            min="1"
            step="1"
            value={fulfillment.freightAboveQuantity}
            onChange={(event) => update({ freightAboveQuantity: event.target.value })}
            disabled={disabled}
            className={controlClassName}
            placeholder="e.g. 20 sacks"
          />
        </Col>
      </Row>
      <Row className="g-3 mb-3">
        {[
          ["length", "Packed length (cm)"],
          ["width", "Packed width (cm)"],
          ["height", "Packed height (cm)"],
        ].map(([key, label]) => (
          <Col md={4} key={key}>
            <Form.Label className={labelClassName}>{label}</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.1"
              value={fulfillment.packageDimensionsCm[key]}
              onChange={(event) => updateDimension(key, event.target.value)}
              disabled={disabled}
              className={controlClassName}
            />
          </Col>
        ))}
      </Row>
      <Row className="g-3 align-items-end">
        <Col md={4}>
          <Form.Label className={labelClassName}>Required vehicle</Form.Label>
          <AppSelect block
            value={fulfillment.requiredCarrierType}
            onChange={(event) => update({ requiredCarrierType: event.target.value })}
            disabled={disabled}
            className={controlClassName}
          >
            <option value="ANY">Any suitable vehicle</option>
            <option value="MOTORCYCLE">Motorcycle</option>
            <option value="CAR">Car</option>
            <option value="VAN">Van</option>
            <option value="TRUCK">Truck</option>
          </AppSelect>
        </Col>
        <Col md={4}>
          <Form.Label className={labelClassName}>Handling class</Form.Label>
          <AppSelect block
            value={fulfillment.handlingClass}
            onChange={(event) => update({ handlingClass: event.target.value })}
            disabled={disabled}
            className={controlClassName}
          >
            <option value="STANDARD">Standard</option>
            <option value="FRAGILE">Fragile</option>
            <option value="UPRIGHT">Keep upright</option>
            <option value="COLD_CHAIN">Cold chain</option>
            <option value="HAZARDOUS">Hazardous goods</option>
          </AppSelect>
        </Col>
        <Col md={4}>
          <Form.Check
            type="switch"
            id="requires-team-lift"
            label="Requires a two-person lift"
            checked={fulfillment.requiresTeamLift}
            onChange={(event) => update({ requiresTeamLift: event.target.checked })}
            disabled={disabled}
          />
        </Col>
      </Row>
    </>
  );
}
